// @ts-nocheck

import React, { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MuiAccordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import styles from './accordion.module.css';

/**
 * Accordion item interface
 * @typedef {Object} AccordionItem
 * @property {string|React.ReactNode} title - Title content
 * @property {string|React.ReactNode} content - Content to display when expanded
 * @property {string} [id] - Unique identifier
 * @property {boolean} [disabled] - Whether item is disabled
 * @property {boolean} [disableGutters] - Whether to disable gutters
 * @property {number} [elevation] - Elevation value
 * @property {boolean} [square] - Whether to remove rounded corners
 * @property {Object} [transitionProps] - Props for transition component
 * @property {React.ReactNode} [expandIcon] - Custom expand icon
 * @property {Object} [iconButtonProps] - Props for icon button
 * @property {string} [titleVariant] - Typography variant for title
 */

interface AccordionProps {
  items: Array<AccordionItem>;
  summary?: (item: AccordionItem, index: number) => React.ReactNode;
  details?: (item: AccordionItem, index: number) => React.ReactNode;
  allowMultiple?: boolean;
  defaultExpanded?: string | null;
  customStyles?: {
    container?: string;
    accordion?: string;
    summary?: string;
    title?: string;
    details?: string;
    content?: string;
  };
  onChange?: (panel: string, isExpanded: boolean) => void;
  expandIcon?: React.ReactNode;
  disabled?: boolean;
}

/**
 * Reusable Accordion component with customizable props
 *
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of accordion items with title and content
 * @param {Function} props.summary - Custom summary renderer
 * @param {Function} props.details - Custom details renderer
 * @param {boolean} props.allowMultiple - Allow multiple items to be expanded at once
 * @param {string} props.defaultExpanded - ID of the initially expanded item
 * @param {Object} props.customStyles - Custom styles for different parts of the accordion
 * @param {Function} props.onChange - Callback when accordion expansion state changes
 * @param {React.ReactNode} props.expandIcon - Custom expand icon
 * @param {boolean} props.disabled - Whether the accordion is disabled
 * @returns {React.ReactElement}
 */
const AtomAccordion: React.FC<AccordionProps> = ({
  items = [],
  summary,
  details,
  allowMultiple = false,
  defaultExpanded = null,
  customStyles = {},
  onChange = () => {},
  expandIcon = <ExpandMoreIcon />,
  disabled = false,
}) => {
  // Initialize expanded state based on defaultExpanded
  const initialState = allowMultiple
    ? defaultExpanded
      ? [defaultExpanded]
      : []
    : defaultExpanded || false;

  const [expanded, setExpanded] = useState(initialState);

  const handleChange = panel => (event, isExpanded) => {
    // Handle expansion state changes based on allowMultiple setting
    if (allowMultiple) {
      setExpanded(prev => {
        if (isExpanded) {
          return [...prev, panel];
        } else {
          return prev.filter(item => item !== panel);
        }
      });
    } else {
      setExpanded(isExpanded ? panel : false);
    }

    // Call custom onChange handler with updated state
    onChange(panel, isExpanded);
  };

  // Check if a panel is expanded
  const isExpanded = panel => {
    if (allowMultiple) {
      return expanded.includes(panel);
    }
    return expanded === panel;
  };

  return (
    <div className={`${styles.accordionContainer} ${customStyles.container || ''}`}>
      {items.map((item, index) => {
        const panelId = item.id || `panel-${index}`;

        // Handle custom summary & details rendering
        const summaryContent = summary ? (
          summary(item, index)
        ) : typeof item.title === 'string' ? (
          <Typography
            className={`${styles.accordionTitle} ${customStyles.title || ''}`}
            variant={item.titleVariant || 'subtitle1'}
          >
            {item.title}
          </Typography>
        ) : (
          item.title
        );

        const detailsContent = details ? (
          details(item, index)
        ) : typeof item.content === 'string' ? (
          <Typography className={`${styles.accordionContent} ${customStyles.content || ''}`}>
            {item.content}
          </Typography>
        ) : (
          item.content
        );

        return (
          <MuiAccordion
            key={panelId}
            expanded={isExpanded(panelId)}
            onChange={handleChange(panelId)}
            className={`${styles.accordion} ${customStyles.accordion || ''}`}
            disabled={item.disabled || disabled}
            disableGutters={item.disableGutters}
            elevation={item.elevation !== undefined ? item.elevation : 1}
            square={item.square}
            TransitionProps={item.transitionProps}
          >
            <AccordionSummary
              expandIcon={item.expandIcon || expandIcon}
              aria-controls={`${panelId}-content`}
              id={`${panelId}-header`}
              className={`${styles.accordionSummary} ${customStyles.summary || ''}`}
              IconButtonProps={item.iconButtonProps}
            >
              {summaryContent}
            </AccordionSummary>
            <AccordionDetails
              className={`${styles.accordionDetails} ${customStyles.details || ''}`}
            >
              {detailsContent}
            </AccordionDetails>
          </MuiAccordion>
        );
      })}
    </div>
  );
};

export default AtomAccordion;
